import { Component, OnInit } from '@angular/core';
import {
  ResponseMessageStatus,
  ResponseStatus,
} from 'src/app/shared/enums';
import * as day from 'dayjs';
import { BadeLoftService } from '../bade-loft.service';
import { CommonService } from 'src/app/services/common.service';

/**
 * Sync Dashboard (Phase 22, OBS-01) — cloned from IntegrationStatusComponent's
 * filter/sort/date-range/infinite-scroll pattern for the activity table, and
 * ConnectionStatusComponent's nz-card usage for the connector health cards.
 *
 * IMPORTANT (D-06): this component applies NO client-side redaction of any kind.
 * Error detail returned by the API is rendered exactly as received — that
 * cleanup step happens only on the server, before the response ever reaches here.
 */
@Component({
  selector: 'app-sync-dashboard',
  templateUrl: './sync-dashboard.component.html',
  styleUrls: ['./sync-dashboard.component.css'],
})
export class SyncDashboardComponent implements OnInit {
  //#region health cards
  // Avalara is intentionally absent: it is not a BadeLoft connector. The client configures Avalara
  // inside Shopify and this app never calls it -- tax simply arrives on the order. The old hard-coded
  // "Not configured" card reported nothing and told ops tax was off while it was working (QA bug #3).
  connectorOrder: string[] = ['Shopify', 'QBO', 'AfterShip'];
  healthCards: any[] = [];
  isHealthLoaded: boolean = false;
  lastUpdated: any = null;
  //#endregion

  //#region activity table state (cloned from IntegrationStatusComponent)
  // 25, not 100. Originally because every row shipped its raw payload and the response was linear in
  // this number (19.1s / 1.96 MB at 100); the payload now loads lazily on expand, but a small page
  // still keeps first paint fast and infinite scroll fetches the rest on demand.
  pageSize: number = 25;
  // Keyset paging: there is no page number and no total. The cursor is simply the last row already
  // rendered, and the scroll stops when the server returns a short page. `totalPage` is no longer
  // read -- it cannot be honoured mid-scroll once reads are cursor-based.
  hasMore: boolean = true;
  isActivityLoaded: boolean = false;
  isApiRunning: any;
  lst_Activity: any[] = [];
  expandSet: Set<number> = new Set<number>();
  filterConnector: string = 'All';
  filterStatus: string = 'All';
  filterDate: any = [];
  // ngxDaterangepickerMd binds to `selected` (NOT filterDate) and expects {startDate, endDate}
  // Dates -- the `{start, end}` shape it was declared with never populated the input, so the
  // picker rendered its bare placeholder. Same shape shipped.component.ts uses for this picker.
  selected: { startDate: Date; endDate: Date };
  ranges: any = {
    Today: [day(), day()],
    Yesterday: [day().subtract(1, 'days'), day().subtract(1, 'days')],
    Tomorrow: [day().add(1, 'days'), day().add(1, 'days')],
    'Last 7 Days': [day().subtract(6, 'days'), day()],
    'Last 30 Days': [day().subtract(29, 'days'), day()],
    'This Month': [day().startOf('month'), day().endOf('month')],
    'Last Month': [
      day().subtract(1, 'month').startOf('month'),
      day().subtract(1, 'month').endOf('month'),
    ],
  };
  //#endregion

  constructor(private _blS: BadeLoftService, public _cS: CommonService) {}

  ngOnInit(): void {
    // Default the activity window to today only (matches the backend default in
    // SyncDashboardService.GetSyncActivity). `selected` must be set alongside filterDate or the
    // picker input shows only its placeholder. filterDate holds query-string-ready ISO strings;
    // bade-loft.service.ts interpolates them straight into the URL, so raw date objects there send
    // literal "undefined".
    const today = day();

    this.selected = {
      startDate: today.startOf('day').toDate(),
      endDate: today.endOf('day').toDate(),
    };
    this.filterDate = [this.toIsoBound(today, false), this.toIsoBound(today, true)];
    this.loadConnectorHealth();
    this.getSyncActivityList();
  }

  refresh(): void {
    this.loadConnectorHealth();
    this.lst_Activity = [];
    this.hasMore = true;
    this.expandSet.clear();
    if (this.isApiRunning) this.isApiRunning.unsubscribe();
    this.getSyncActivityList(true);
  }

  loadConnectorHealth(): void {
    this.isHealthLoaded = true;
    this._blS.GetSyncConnectorHealth(false).subscribe({
      next: (response: any) => {
        // Reset FIRST, on every path -- a stuck Refresh button (nzLoading disables it) is worse
        // than a stale health card (QA bug #6, on the very button used to re-test QA bug #5).
        this.isHealthLoaded = false;

        if (response.responseStatus == ResponseStatus.Success) {
          const data = response.result || [];
          // Always render exactly 3 cards, in a fixed order, even if the API
          // has no row yet for a connector.
          this.healthCards = this.connectorOrder.map((name: string) => {
            const found = data.find((x: any) => x.connector === name);
            return (
              found || {
                connector: name,
                status: 'NotConfigured',
                lastSyncUtc: null,
                message: '',
              }
            );
          });
          this.lastUpdated = day();
        } else {
          this._cS.displayMessage(
            ResponseMessageStatus.error,
            response.message
          );
        }
      },
      error: () => {
        this.isHealthLoaded = false;
        this._cS.displayMessage(
          ResponseMessageStatus.error,
          'Could not load connector health. Please try again.'
        );
      },
    });
  }

  onScrollDown() {
    // Guard on isActivityLoaded too: infinite-scroll fires repeatedly while a fetch is in flight, and
    // without this every one of those would re-send the SAME cursor and duplicate the page.
    if (!this.hasMore || this.isActivityLoaded) return;
    this.getSyncActivityList();
  }

  getSyncActivityList(isRefresh: boolean = false) {
    this.isActivityLoaded = true;

    // The cursor IS the last row on screen. Empty on the first load of a filter set, which the server
    // reads as "first page".
    const lastRow = this.lst_Activity[this.lst_Activity.length - 1];

    this.isApiRunning = this._blS
      .GetSyncActivity(
        this.filterConnector === 'All' ? '' : this.filterConnector,
        this.filterStatus === 'All' ? '' : this.filterStatus,
        this.filterDate[0],
        this.filterDate[1],
        this.pageSize,
        lastRow ? lastRow.timestamp : '',
        lastRow ? lastRow.sourceId : '',
        false
      )
      .subscribe({
        next: (response: any) => {
          // Reset FIRST, on every path -- a stuck skeleton is worse than an empty table.
          this.isActivityLoaded = false;

          if (response.responseStatus == ResponseStatus.Success) {
            var data = response.result.records || [];
            // Short page => the end. This replaces the old totalPage comparison, which cannot be
            // honoured once reads are cursor-based rather than offset-based.
            this.hasMore = data.length === this.pageSize;
            if (data.length > 0) {
              data.forEach((x: any) => {
                this.lst_Activity.push(x);
              });
            }
            if (isRefresh) {
              this._cS.displayMessage(
                ResponseMessageStatus.success,
                'Data sync successfully'
              );
            }
          } else {
            this._cS.displayMessage(ResponseMessageStatus.error, response.message);
          }
        },
        error: () => {
          this.isActivityLoaded = false;
          this._cS.displayMessage(
            ResponseMessageStatus.error,
            'Could not load sync activity. Please try again.'
          );
        },
      });
  }

  onConnectorFilterChange() {
    this.lst_Activity = [];
    this.hasMore = true;
    this.expandSet.clear();
    if (this.isApiRunning) this.isApiRunning.unsubscribe();
    this.getSyncActivityList();
  }

  onStatusFilterChange() {
    this.lst_Activity = [];
    this.hasMore = true;
    this.expandSet.clear();
    if (this.isApiRunning) this.isApiRunning.unsubscribe();
    this.getSyncActivityList();
  }

  onDateChange(result: any) {
    // This is wired to the picker's (ngModelChange), which fires for PROGRAMMATIC writes too --
    // including ngOnInit setting `selected` -- and the picker's init/clear emission carries no
    // dates. Without this guard that emission wrote `undefined` into both filterDate slots, then
    // unsubscribed the in-flight ngOnInit request (showing as a CANCELLED call in the network tab)
    // and re-issued it unfiltered, so every page load cost two requests and the slow one won.
    // Siblings (shipped/status .component.ts) substitute today here; our default window is already
    // today, so keeping filterDate as-is is the same result minus the redundant round trip.
    if (!result?.startDate || !result?.endDate) return;

    const from = this.toIsoBound(result.startDate, false);
    const to = this.toIsoBound(result.endDate, true);

    // NO-OP GUARD -- do not remove. ngOnInit's own `selected` assignment echoes straight back
    // through (ngModelChange) carrying the dates we just set. The emptiness check above does not
    // catch it (that echo HAS dates), and until the moment() fix above it was masked by the
    // TypeError aborting this handler before it could do damage. Acting on that echo unsubscribes
    // the in-flight ngOnInit request -- a CANCELLED row in the network tab -- and re-issues an
    // identical one, so every page load costs two requests and the user waits for the second.
    // Only a real CHANGE re-queries.
    if (from === this.filterDate[0] && to === this.filterDate[1]) return;

    this.filterDate[0] = from;
    this.filterDate[1] = to;
    this.lst_Activity = [];
    this.hasMore = true;
    this.expandSet.clear();
    if (this.isApiRunning) this.isApiRunning.unsubscribe();
    this.getSyncActivityList();
  }

  /**
   * The one place a date becomes a query-string bound, for BOTH the ngOnInit default and a user pick.
   *
   * Do NOT put this through moment(). ngx-daterangepicker-material v6 is DAYJS-based -- setStartDate /
   * setEndDate emit `dayjs(x).utc(true)` -- so this handler receives a dayjs on a real pick and a plain
   * JS Date on the programmatic echo of ngOnInit's own `selected`. moment() handles the Date but matches
   * none of its input shapes for a dayjs object: it falls through to configFromObject, recognises none of
   * dayjs's `$y/$M/$D` keys and silently returns TODAY AT LOCAL MIDNIGHT. That discarded every pick, so
   * the first apply queried a zero-width window and every apply after it was swallowed by the no-op guard
   * in onDateChange -- reported as "apply the date picker and it doesn't call the API" (2026-07-30).
   * dayjs() normalises both shapes, and it clones rather than mutates (moment's .utc() mutates in place).
   *
   * Format is the picked WALL-CLOCK day labeled Z -- the convention every other page uses
   * (shipped.component.ts: `yyyy-MM-dd` + 'T00:00:00Z') and the one the picker itself emits, since
   * `.utc(true)` keeps local time. Converting local midnight to a UTC instant instead shifted the whole
   * window by the browser's offset (5.5h from IST), so "today" was not today.
   */
  private toIsoBound(value: any, endOfDay: boolean): string {
    return day(value).format('YYYY-MM-DD') + (endOfDay ? 'T23:59:59Z' : 'T00:00:00Z');
  }

  toggleExpand(index: number): void {
    if (this.expandSet.has(index)) {
      this.expandSet.delete(index);
      return;
    }
    this.expandSet.add(index);
    this.loadDetail(this.lst_Activity[index]);
  }

  /**
   * The payload is no longer part of the activity response -- at ~19.6 KB/row it was ~95% of the body
   * for a panel that starts collapsed. Fetch it once, on first expand, and keep it on the row itself
   * (no parallel index->payload map to keep in sync with the infinite-scroll array).
   *
   * Guarded on `detailLoaded`, NOT on the truthiness of errorDetail: the API legitimately returns null
   * when a row's note carries nothing the Summary column does not already show (ToDetailPayload), and a
   * truthiness guard would re-fetch that row on every single expand.
   */
  private loadDetail(row: any): void {
    if (!row || row.detailLoaded || row.detailLoading) return;

    row.detailLoading = true;
    row.detailError = false;
    this._blS.GetSyncActivityDetail(row.source, row.sourceId, false).subscribe({
      next: (response: any) => {
        row.detailLoading = false;
        if (response.responseStatus == ResponseStatus.Success) {
          row.errorDetail = response.result;
          row.detailLoaded = true;
        } else {
          row.detailError = true;
        }
      },
      // No displayMessage here: a failed payload fetch is scoped to one expanded row and says so
      // inline. A global error toast per row would be noise on a page whose whole job is showing
      // failures -- and collapsing/reopening the row retries.
      error: () => {
        row.detailLoading = false;
        row.detailError = true;
      },
    });
  }

  //#region pill class helpers (UI-SPEC status/health palette)
  getStatusPillClass(status: string): string {
    switch (status) {
      case 'Pending':
        return 'sync-pending';
      case 'Invoiced':
        return 'sync-invoiced';
      case 'Paid':
        return 'paid';
      case 'Refunded':
        return 'sync-refunded';
      case 'Voided':
        return 'sync-voided';
      case 'Error':
        return 'sync-error';
      case 'Synced':
        return 'sync-synced';
      case 'Skipped':
        return 'sync-skipped';
      // "Not Synced" is NOT the same thing as "Skipped": Skipped is the retail-price sync deciding a SKU
      // needed no change (thousands of rows, all benign), while Not Synced is a QBO invoice that produced
      // no Shopify order at all. Sharing a pill made the second invisible inside the first.
      case 'NotSynced':
        return 'sync-notsynced';
      default:
        return 'sync-pending';
    }
  }

  getHealthPillClass(status: string): string {
    switch (status) {
      case 'Healthy':
        return 'sync-health-ok';
      case 'Degraded':
        return 'sync-health-degraded';
      case 'Down':
        return 'sync-health-down';
      default:
        return 'sync-health-unknown';
    }
  }

  getHealthPillLabel(status: string): string {
    switch (status) {
      case 'Healthy':
        return 'Healthy';
      case 'Degraded':
        return 'Degraded';
      case 'Down':
        return 'Down';
      default:
        return 'Not configured';
    }
  }
  //#endregion
}
